import { Box, Button, Flex, Text, useToast } from '@chakra-ui/react';
import {
  Role,
  userApi,
  UserListItem,
  UserSearchQuery,
  UserType,
} from '@texas/api/endpoints/userApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { clientEndpoints } from '@texas/clientEndpoints';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { ServerError } from '@texas/types';
import { getEnumValues } from '@texas/utils/helpers/enumHelpers';
import { request } from '@texas/utils/helpers/httpHelpers';
import {
  roleFriendlyName,
  userTypeFriendlyName,
} from '@texas/utils/helpers/roleHelpers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchFilterInput } from '../SearchFilterInput';
import { BodyOverride } from '../shared/BodyOverride';
import { RoleRestrict } from '../shared/RoleRestrict';
import { DataTableContainer } from '../shared/dataTable/DataTableContainer';
import { VerifyDialogWithRequest } from '../shared/dialog/VerifyDialogWithRequest';
import { EntityDirectoryPage } from '../shared/entityDirectory/EntityDirectoryPage';
import { EntityDirectorySelectFilter } from '../shared/entityDirectory/EntityDirectorySelectFilter';
import { TexasSwitch } from '../shared/form/TexasSwitch';
import { defaultIconSize, Icons } from '../shared/Icons';
import { ArchiveUser } from './ArchiveUser';
import { usersColumns } from './Columns';
import { CreateUser } from './CreateUser';
import { EditUser } from './EditUser';
import { userEvents } from './events';
import { SelectRole } from './SelectRole';

const defaultSearchQuery: UserSearchQuery = {
  sortBy: 'name',
  sortDesc: false,
  searchTerm: '',
  includeArchived: false,
  branchId: null,
  role: null,
  type: null,
  page: 1,
  pageSize: 24,
};

export function Users() {
  const { data, refetch, loading, error } = useApiResource(userApi.searchUsers);
  const toast = useToast();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] =
    useState<UserSearchQuery>(defaultSearchQuery);
  const {
    branchesData: [branches, getBranches],
  } = useMetadata();

  useEffect(() => {
    getBranches();
  }, [getBranches]);

  const {
    value: archiveValue,
    isOpen: isArchiveOpen,
    onOpen: onArchiveOpen,
    onClose: onArchiveClose,
  } = useValueDisclosure<UserListItem>();
  const {
    value: editValue,
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useValueDisclosure<number>();
  const {
    value: roleEdit,
    isOpen: isRoleEditOpen,
    onOpen: onRoleEditOpen,
    onClose: onRoleEditClose,
  } = useValueDisclosure<number>();
  const {
    value: sendWelcomeEmail,
    isOpen: isSendWelcomeEmailOpen,
    onOpen: onSendWelcomeEmailOpen,
    onClose: onSendWelcomeEmailClose,
  } = useValueDisclosure<number>();

  const logoutDevices = useCallback(
    async (id: number) => {
      const response = await userApi.signOutDevices(id);

      if (response.hasError) {
        toast({
          title: t('auth.deviceLogout.failed'),
          description: response.error.message,
          status: 'error',
          isClosable: true,
        });
        return;
      }

      toast({
        title: t('auth.deviceLogout.titleSuccess'),
        description: t('auth.deviceLogout.descSuccess'),
        status: 'success',
        isClosable: true,
      });
    },
    [t, toast],
  );

  const refetchCallback = useCallback(
    () => refetch(searchQuery),
    [refetch, searchQuery],
  );

  useEffect(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  useEffect(() => {
    const unsubscribeCreated = userEvents.newUserCreated.subscribe(() => {
      refetchCallback();
    });
    const unsubscribeUpdated = userEvents.userUpdated.subscribe(() => {
      refetchCallback();
    });
    const unsubscribeCurrentUser = userEvents.currentUserUpdated.subscribe(
      () => {
        refetchCallback();
      },
    );

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeCurrentUser();
    };
  }, [refetchCallback]);

  const { request: restoreUserRequest } = useApiRequest(userApi.restore);

  const performRestore = useCallback(
    async (user: UserListItem) => {
      await request(
        restoreUserRequest,
        [user.id],
        () => {
          toast({
            title: t('general.entityRestored', {
              name: user.name || user.email,
            }),
            status: 'success',
            isClosable: true,
          });
          refetch(searchQuery);
        },
        (restoreError: ServerError) => {
          toast({
            title: t('general.restoreFailed'),
            description: restoreError.message,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [restoreUserRequest, toast, t, refetch, searchQuery],
  );

  const columns = useMemo(
    () =>
      usersColumns(
        t,
        onArchiveOpen,
        performRestore,
        onEditOpen,
        onRoleEditOpen,
        onSendWelcomeEmailOpen,
        logoutDevices,
        branches,
      ),
    [
      t,
      onArchiveOpen,
      performRestore,
      onEditOpen,
      onRoleEditOpen,
      onSendWelcomeEmailOpen,
      logoutDevices,
      branches,
    ],
  );

  const roleOptions = useMemo(
    () =>
      getEnumValues(Role).map((role) => ({
        label: roleFriendlyName(role as Role, t),
        value: role as Role,
      })),
    [t],
  );
  const branchOptions = useMemo(
    () =>
      branches.map((branch) => ({
        label: `${branch.name} (${branch.identifier})`,
        value: branch.id,
      })),
    [branches],
  );
  const userTypeOptions = useMemo(
    () =>
      getEnumValues(UserType).map((userType) => ({
        label: userTypeFriendlyName(userType as UserType, t),
        value: userType as UserType,
      })),
    [t],
  );

  const totalItems = data?.totalItems ?? 0;
  const firstItem =
    totalItems === 0 ? 0 : (searchQuery.page - 1) * searchQuery.pageSize + 1;
  const lastItem = Math.min(
    searchQuery.page * searchQuery.pageSize,
    totalItems,
  );

  return (
    <BodyOverride>
      <RoleRestrict allow={[Role.SystemAdministrator]}>
        {archiveValue && (
          <ArchiveUser
            user={archiveValue}
            isOpen={isArchiveOpen}
            onClose={onArchiveClose}
            onArchive={() => refetch(searchQuery)}
          />
        )}
        {editValue && (
          <EditUser id={editValue} isOpen={isEditOpen} onClose={onEditClose} />
        )}
        {roleEdit && (
          <SelectRole
            id={roleEdit}
            onClose={onRoleEditClose}
            isOpen={isRoleEditOpen}
          />
        )}
        {sendWelcomeEmail && (
          <VerifyDialogWithRequest
            headerTitle={t('user.sendWelcomeEmail')}
            secondaryButtonTitle={t('general.close')}
            primaryButtonTitle={t('general.sendEmail')}
            primaryRequest={userApi.sendWelcomeMail}
            args={[sendWelcomeEmail]}
            onSuccessTitle={t('general.emailSent')}
            isOpen={isSendWelcomeEmailOpen}
            onClose={onSendWelcomeEmailClose}
          >
            {t('user.verifySendWelcomeEmail')}
          </VerifyDialogWithRequest>
        )}

        <EntityDirectoryPage
          actions={
            <Flex align="center" gap={2}>
              <Button
                as="a"
                download={true}
                h="42px"
                href={clientEndpoints.downloadGlobalBrandList()}
                leftIcon={<Icons.FileExcelBox boxSize={defaultIconSize} />}
                target="_blank"
                variant="entityDirectorySecondary"
              >
                {t('user.exportGlobalBrandList')}
              </Button>
              <CreateUser
                buttonHeight="42px"
                buttonVariant="entityDirectoryPrimary"
              />
            </Flex>
          }
          description={t('user.directoryDescription')}
          title={t('general.users')}
        >
          <Box
            bg="entityDirectory.surface"
            border="1px solid"
            borderColor="entityDirectory.border"
            minH={{ base: 'auto', lg: '752px' }}
          >
            <Flex
              align={{ base: 'stretch', lg: 'center' }}
              borderBottom="1px solid"
              borderColor="entityDirectory.border"
              direction={{ base: 'column', lg: 'row' }}
              flexWrap="wrap"
              gap={2.5}
              minH="94px"
              px="22px"
              py={5}
            >
              <Box flex="1 1 280px" maxW={{ base: 'full', lg: '356px' }}>
                <SearchFilterInput
                  height="42px"
                  placeholder={t('user.searchUsers')}
                  style="entityDirectory"
                  value={searchQuery.searchTerm}
                  onChange={(searchTerm) =>
                    setSearchQuery({ ...searchQuery, searchTerm, page: 1 })
                  }
                />
              </Box>
              <EntityDirectorySelectFilter
                buttonProps={{
                  h: '42px',
                  justifyContent: 'space-between',
                  w: { base: 'full', sm: '196px' },
                }}
                options={branchOptions}
                placeholder={t('filter.allBranches')}
                value={searchQuery.branchId}
                onChange={(branchId) =>
                  setSearchQuery({ ...searchQuery, branchId, page: 1 })
                }
              />
              <EntityDirectorySelectFilter
                buttonProps={{
                  h: '42px',
                  justifyContent: 'space-between',
                  w: { base: 'full', sm: '184px' },
                }}
                options={roleOptions}
                placeholder={t('filter.allRoles')}
                value={searchQuery.role}
                onChange={(role) =>
                  setSearchQuery({ ...searchQuery, role, page: 1 })
                }
              />
              <EntityDirectorySelectFilter
                buttonProps={{
                  h: '42px',
                  justifyContent: 'space-between',
                  w: { base: 'full', sm: '196px' },
                }}
                options={userTypeOptions}
                placeholder={t('filter.allUserTypes')}
                value={searchQuery.type}
                onChange={(type) =>
                  setSearchQuery({ ...searchQuery, type, page: 1 })
                }
              />
              <Box
                bg="entityDirectory.border"
                display={{ base: 'none', lg: 'block' }}
                h="28px"
                w="1px"
              />
              <TexasSwitch
                checked={searchQuery.includeArchived}
                label={t('general.includeArchived')}
                onChange={(includeArchived) =>
                  setSearchQuery({
                    ...searchQuery,
                    includeArchived,
                    page: 1,
                  })
                }
              />
              <Text
                color="entityDirectory.mutedText"
                fontSize="13px"
                ml={{ base: 0, lg: 'auto' }}
              >
                {t('user.userCount', { count: totalItems })}
              </Text>
            </Flex>

            <DataTableContainer
              bg="entityDirectory.surface"
              tableId="users"
              w="100%"
              datatable={{
                data: data?.items ?? [],
                sorted: {
                  onSortedChange: ({ key, desc }) => {
                    setSearchQuery({
                      ...searchQuery,
                      sortBy: key,
                      sortDesc: desc,
                    });
                  },
                  key: searchQuery.sortBy,
                  desc: searchQuery.sortDesc,
                },
                isLoading: loading,
                columns,
                variant: 'entity-directory',
              }}
              error={error}
              footer={
                <Text color="entityDirectory.mutedText" fontSize="12px">
                  {t('user.showingResults', {
                    first: firstItem,
                    last: lastItem,
                    total: totalItems,
                  })}
                </Text>
              }
              footerVariant="entityDirectoryBordered"
              pagination={{
                totalItems,
                pageSize: searchQuery.pageSize,
                currentPage: searchQuery.page,
                onPageChange: (page) =>
                  setSearchQuery({ ...searchQuery, page }),
                variant: 'entityDirectoryPagination',
              }}
            />
          </Box>
        </EntityDirectoryPage>
      </RoleRestrict>
    </BodyOverride>
  );
}
